/**
 * PaymentStepScreen: the booking flow's payment page, shared by the Mini App's PaymentPage and the
 * studio website's phone checkout and Pay now (shared-client-screens D3, rows P1 to P10). Props
 * only: the pinned photo header, "Complete payment", the hold line, Total due, the methods or the
 * state's notice (deposits, card paused, too close to pay, already paid), the decline, Payments
 * secured by Stripe, the pay bar and the confirming overlay. The surface hands in the card form
 * (and in the app its Venmo rows, row P1), the payment flow's phase (usePaymentFlow) and its
 * handlers; the notices' words come from bookingRules.
 */
import type { ReactNode, Ref } from 'react';
import { BookingHero, type BookingHeroProps } from '@/components/booking/BookingHero';
import { BookingStepFrame } from '@/components/booking/BookingStepFrame';
import { CardPaymentWait } from '@/components/payment/cardPaymentWait';
import { CheckoutOverlay } from '@/components/payment/CheckoutOverlay';
import type { PaymentFlowPhase } from '@/hooks/payment/usePaymentFlow';
import { declineNotice, paymentNotice, type PaymentNotice, type PaymentView } from '@/pages/booking/shared/bookingRules';
import { PhoneCallDetails, usePhoneCallAsk, type PhoneCallProps } from '@/pages/ConfirmPage/PhoneCallRow';
import styles from './PaymentPage.module.css';

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/** The payment page's banner (decline, expired, degrade, hard block, settling). */
export function PaymentBanner({ variant, title, body }: PaymentNotice) {
  return (
    <div className={styles.banner} role="status">
      {variant === 'warning' ? <WarningIcon /> : <InfoIcon />}
      <div>
        <div className={styles.bannerTitle}>{title}</div>
        <div className={styles.bannerBody}>{body}</div>
      </div>
    </div>
  );
}

/** The payment page's placeholder while its read is in flight (row P10). */
export function PaymentStepLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading payment details...</p>
      </div>
    </div>
  );
}

/**
 * The payment page's frame for a step the app lacks (the website's sign-in, a time taken meanwhile):
 * Back, the photo header, the title, an optional total, the surface's own body, and one bar button.
 */
export function PaymentStepFrame({
  hero,
  onBack,
  title,
  total,
  bar,
  children,
}: {
  hero: BookingHeroProps;
  onBack: () => void;
  title?: string;
  total?: { readonly label: string; readonly value: string } | null;
  bar?: { readonly label: ReactNode; readonly onPress: () => void; readonly disabled?: boolean; readonly className?: string; readonly note?: ReactNode } | null;
  children?: ReactNode;
}) {
  return (
    <BookingStepFrame
      styles={styles}
      hero={<BookingHero {...hero} />}
      onBack={onBack}
      bar={
        bar && (
          <div className={styles.formbar}>
            <button type="button" className={`${styles.payBtn} ${bar.className ?? ''}`} disabled={bar.disabled} onClick={bar.onPress}>
              {bar.label}
            </button>
            {bar.note}
          </div>
        )
      }
    >
      {title && <h1 className={styles.ptitle}>{title}</h1>}
      {total && (
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>{total.label}</span>
          <span className={styles.totalValue}>{total.value}</span>
        </div>
      )}
      {children}
    </BookingStepFrame>
  );
}

export interface PaymentStepScreenProps {
  hero: BookingHeroProps;
  onBack: () => void;
  backDisabled?: boolean;
  /** The scroller, for the surface's keyboard rail. */
  scrollRef?: Ref<HTMLDivElement>;
  /** The hold line ("Time slot reserved until 6:15 PM"); null without one. */
  reserved: string | null;
  /** A by-phone booking paid now: the call line between the hold line and Total due (service-by-phone D5). */
  phoneCall?: PhoneCallProps | null;
  /** The amount due, "$125.00". */
  amount: string;
  /** Which methods show (paymentView). */
  view: PaymentView;
  /** The payment flow's phase (usePaymentFlow). */
  phase: PaymentFlowPhase;
  businessName: string;
  /** Pay now: the booking already exists, so the card-paused notice keeps it (paymentNotice). */
  booked?: boolean;
  /** The payable views' method section: the card form, and in the app its Venmo rows (row P1). */
  methods: ReactNode;
  /** Notices under the methods that only one surface has (the app's Venmo settling and error). */
  notices?: ReactNode;
  /** The card's decline words; null when none. */
  decline: string | null;
  /** Another method sits beside the card (the app's Venmo), for the decline's way out (row P6). */
  otherMethods: boolean;
  /** The pay bar's button: Confirm & pay by card, or the app's Confirm booking for Venmo. */
  pay: { readonly method: 'CARD' | 'VENMO'; readonly onPress: () => void; readonly disabled: boolean };
  /** Pick a new time, when too close to the start to pay: one step back. */
  onPickNewTime: () => void;
  pickNewTimeDisabled?: boolean;
  /** The slow confirm's Done: the app closes Telegram, the website opens the studio's home. */
  onSlowDone?: () => void;
}

export function PaymentStepScreen({
  hero,
  onBack,
  backDisabled,
  scrollRef,
  reserved,
  phoneCall,
  amount,
  view,
  phase,
  businessName,
  booked,
  methods,
  notices,
  decline,
  otherMethods,
  pay,
  onPickNewTime,
  pickNewTimeDisabled,
  onSlowDone,
}: PaymentStepScreenProps) {
  const tooClose = phase === 'windowTooClose';
  const allSet = phase === 'benignTerminal';
  const confirming = phase === 'confirming' || phase === 'slowConfirm';
  // The call line's number is missing or being changed: paying waits for it (D5).
  const ask = usePhoneCallAsk(phoneCall);

  const renderMethods = () => {
    if (view === 'depositBlocked' || view === 'hardBlock') return <PaymentBanner {...paymentNotice(view, businessName, { booked })} />;
    if (view === 'cardUnavailableDegrade') {
      return (
        <>
          {methods}
          <PaymentBanner {...paymentNotice(view, businessName)} />
        </>
      );
    }
    return methods;
  };

  const renderPayButton = () => {
    if (tooClose) {
      return (
        <button type="button" className={styles.payBtn} onClick={onPickNewTime} disabled={pickNewTimeDisabled}>
          Pick a new time
        </button>
      );
    }
    if (view === 'depositBlocked') {
      return (
        <button type="button" className={styles.payBtnDisabled} disabled>
          Deposits unavailable
        </button>
      );
    }
    if (view === 'hardBlock') {
      return (
        <button type="button" className={styles.payBtnDisabled} disabled>
          Card payments unavailable
        </button>
      );
    }
    if (pay.method === 'CARD') {
      return (
        <button type="button" className={styles.payBtn} onClick={pay.onPress} disabled={pay.disabled || ask.asking}>
          Confirm &amp; pay {amount}
          <ArrowRightIcon />
        </button>
      );
    }
    // Venmo commit: the accent "Confirm booking" (it books; it does not open Venmo).
    return (
      <button type="button" className={styles.payBtn} onClick={pay.onPress} disabled={pay.disabled || ask.asking}>
        <CheckIcon />
        Confirm booking
      </button>
    );
  };

  return (
    <BookingStepFrame
      styles={styles}
      hero={<BookingHero {...hero} />}
      onBack={onBack}
      backDisabled={backDisabled}
      scrollRef={scrollRef}
      // The pay bar is hidden only for the "You're all set" state.
      bar={!allSet && <div className={styles.formbar}>{renderPayButton()}</div>}
      overlay={
        // The post-confirm overlay: confirming, then the slow confirm.
        confirming && <CheckoutOverlay variant={phase === 'slowConfirm' ? 'slow' : 'confirming'} amountText={`${amount} paid via card`} onDone={onSlowDone} />
      }
    >
      <div className={styles.ptitle}>Complete payment</div>

      {reserved && !tooClose && !allSet && (
        <div className={styles.reservedInfo}>
          <ClockIcon />
          {reserved}
        </div>
      )}

      {phoneCall && !tooClose && !allSet && <PhoneCallDetails phoneCall={phoneCall} changing={ask.changing} onChangingChange={ask.setChanging} />}

      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>Total due</span>
        <span className={styles.totalValue}>{amount}</span>
      </div>

      {tooClose || allSet ? (
        <PaymentBanner {...paymentNotice(tooClose ? 'windowTooClose' : 'benignTerminal', businessName)} />
      ) : (
        <div>
          <div className={styles.sectionLabel}>Payment method</div>
          {/* The card waits for the number to call while it is asked for (D5). */}
          <CardPaymentWait.Provider value={ask.asking}>
            <div className={styles.paySec}>{renderMethods()}</div>
          </CardPaymentWait.Provider>
          {/* B9 decline (S4): the same payment can be tried again in place. */}
          {decline && <PaymentBanner {...declineNotice(decline, otherMethods)} />}
          {notices}
        </div>
      )}

      <div className={styles.secure}>
        <LockIcon />
        Payments secured by Stripe
      </div>
    </BookingStepFrame>
  );
}
